import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import {
  RANGE_KIND,
  REPORT_DELIVERY,
  REPORT_SCOPE,
  companyReportApi
} from '../../api/companyReportApi';

/**
 * Period + delivery picker for a dashboard report.
 *
 * Two things it deliberately does NOT do:
 *
 *  - it does not re-ask for department/location/age/gender. The report
 *    inherits whatever the dashboard is already filtered to, and shows that
 *    back read-only, so an admin cannot end up with a PDF covering a
 *    different population from the screen they clicked download on.
 *  - it does not offer a free-text recipient. "Email it to me" goes to the
 *    logged-in admin's own address, decided by the backend from the token.
 *
 * Which button appears depends on scope. One feature renders inside the
 * request and saves straight to disk. A whole section or the whole dashboard
 * is thirty aggregations and a PDF render, so it is queued and collected from
 * the Reports page.
 */

const RADIO_ROW =
  'flex items-center gap-2 text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg cursor-pointer';

const SELECT_CLASS =
  'w-full h-10 px-3 rounded-lg border border-shoorah-inputBorder dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg appearance-none focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active disabled:opacity-40';

const PRIMARY_BTN =
  'flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-shoorah-active text-white text-sm font-medium hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-shoorah-active disabled:opacity-50 disabled:cursor-not-allowed';

const SECONDARY_BTN =
  'flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-lg border border-shoorah-inputBorder dark:border-shoorah-darkBgTabColor text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active disabled:opacity-50 disabled:cursor-not-allowed';

/** Human summary of the filters the report will inherit. */
const describeFilters = (filters) => {
  const parts = [
    filters?.department && `Department: ${filters.department}`,
    filters?.location && `Location: ${filters.location}`,
    filters?.age && `Age: ${filters.age}`,
    filters?.ethinicity && `Ethnicity: ${filters.ethinicity}`
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'All employees';
};

const ReportDownloadDialog = ({
  open,
  onClose,
  scope,
  featureKey,
  featureLabel,
  plane,
  planeLabel,
  filters,
  onQueued,
  api = companyReportApi,
  subject = null
}) => {
  const [options, setOptions] = useState(null);
  const [kind, setKind] = useState(RANGE_KIND.DAYS);
  const [days, setDays] = useState(30);
  const [monthValue, setMonthValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  // The selectable periods depend on when the company signed up, so they come
  // from the server rather than being generated here.
  useEffect(() => {
    if (!open || options) return;
    let cancelled = false;
    api.fetchReportOptions().then((data) => {
      if (cancelled || !data) return;
      setOptions(data);
      if (data.periods?.months?.length) {
        const newest = data.periods.months[0];
        setMonthValue(`${newest.year}-${newest.month}`);
      }
      if (data.periods?.years?.length) setYearValue(String(data.periods.years[0]));
    });
    return () => {
      cancelled = true;
    };
  }, [open, options, api]);

  // Clear the outcome each time it reopens, so a previous success or failure
  // is never mistaken for this one.
  useEffect(() => {
    if (open) {
      setError(null);
      setDone(null);
      setBusy(null);
    }
  }, [open]);

  const title = useMemo(() => {
    if (scope === REPORT_SCOPE.FEATURE) return featureLabel || 'Feature';
    if (scope === REPORT_SCOPE.PLANE) return planeLabel || 'Section';
    return 'Whole dashboard';
  }, [scope, featureLabel, planeLabel]);

  const period = useMemo(() => {
    if (kind === RANGE_KIND.MONTH) {
      const [year, month] = String(monthValue).split('-').map(Number);
      return { kind, month, year };
    }
    if (kind === RANGE_KIND.YEAR) return { kind, year: Number(yearValue) };
    return { kind, days };
  }, [kind, days, monthValue, yearValue]);

  // Only a single feature can be rendered inside the request.
  const canDownloadDirectly = scope === REPORT_SCOPE.FEATURE;

  const body = (delivery) =>
    api.buildReportBody({ scope, featureKey, plane, period, filters, delivery, subject });

  const run = async (action) => {
    setBusy(action);
    setError(null);
    setDone(null);

    if (action === 'download' && canDownloadDirectly) {
      const result = await api.downloadFeatureReport(body());
      setBusy(null);
      if (result.error) return setError(result.error);
      setDone('Your report has been downloaded.');
      return onClose?.();
    }

    const result = await api.requestReport(
      body(action === 'email' ? REPORT_DELIVERY.EMAIL : REPORT_DELIVERY.DOWNLOAD)
    );
    setBusy(null);
    if (result.error) return setError(result.error);

    onQueued?.(result.job);
    setDone(
      action === 'email'
        ? 'We are preparing your report and will email it to you when it is ready.'
        : 'We are preparing your report. It will appear on the Reports page shortly.'
    );
    return undefined;
  };

  const months = options?.periods?.months || [];
  const years = options?.periods?.years || [];
  const presets = options?.periods?.presets || [
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
    { days: 180, label: 'Last 6 months' },
    { days: 365, label: 'Last 1 year' }
  ];

  return (
    <Transition.Root appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-shoorah-darkBgColor text-left shadow-xl transition-all">
                <div className="p-6 flex flex-col gap-5 max-h-[85vh] overflow-y-auto hide-scrollbar">
                  {/* header */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg"
                      >
                        Download report
                      </Dialog.Title>
                      <p className="text-xs text-shoorah-gray3 mt-[2px]">{title}</p>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Close"
                      className="p-1 rounded-full text-shoorah-gray4 dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* period */}
                  <fieldset className="flex flex-col gap-3">
                    <legend className="text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg mb-2">
                      Period
                    </legend>

                    <div className="grid grid-cols-2 gap-2">
                      {presets.map((preset) => (
                        <label key={preset.days} className={RADIO_ROW}>
                          <input
                            type="radio"
                            name="report-period"
                            className="accent-shoorah-active"
                            checked={kind === RANGE_KIND.DAYS && days === preset.days}
                            onChange={() => {
                              setKind(RANGE_KIND.DAYS);
                              setDays(preset.days);
                            }}
                          />
                          {preset.label}
                        </label>
                      ))}
                    </div>

                    <div className="flex items-center gap-3">
                      <label className={`${RADIO_ROW} w-32 shrink-0`}>
                        <input
                          type="radio"
                          name="report-period"
                          className="accent-shoorah-active"
                          checked={kind === RANGE_KIND.MONTH}
                          onChange={() => setKind(RANGE_KIND.MONTH)}
                        />
                        A month
                      </label>
                      <select
                        className={SELECT_CLASS}
                        disabled={kind !== RANGE_KIND.MONTH || !months.length}
                        value={monthValue}
                        onChange={(event) => setMonthValue(event.target.value)}
                      >
                        {months.map((month) => (
                          <option key={`${month.year}-${month.month}`} value={`${month.year}-${month.month}`}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className={`${RADIO_ROW} w-32 shrink-0`}>
                        <input
                          type="radio"
                          name="report-period"
                          className="accent-shoorah-active"
                          checked={kind === RANGE_KIND.YEAR}
                          onChange={() => setKind(RANGE_KIND.YEAR)}
                        />
                        A year
                      </label>
                      <select
                        className={SELECT_CLASS}
                        disabled={kind !== RANGE_KIND.YEAR || !years.length}
                        value={yearValue}
                        onChange={(event) => setYearValue(event.target.value)}
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Only periods since signup are offered, so nobody exports
                        a year of emptiness that predates their account. */}
                    <p className="text-[11px] text-shoorah-gray3">
                      Periods run from when your company joined Shoorah.
                    </p>
                  </fieldset>

                  {/* inherited filters */}
                  <div className="rounded-lg border border-shoorah-borderGray dark:border-shoorah-darkBgTabColor p-3">
                    <p className="text-xs text-shoorah-gray3">This report will cover</p>
                    <p className="text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg mt-1">
                      {describeFilters(filters)}
                    </p>
                    <p className="text-[11px] text-shoorah-gray3 mt-1">
                      Taken from your dashboard filters, and printed on the report.
                    </p>
                  </div>

                  {error ? (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {error}
                    </p>
                  ) : null}
                  {done ? (
                    <p className="text-sm text-green-700 dark:text-green-400" role="status">
                      {done}
                    </p>
                  ) : null}

                  {!canDownloadDirectly ? (
                    <p className="text-[11px] text-shoorah-gray3">
                      Reports covering more than one feature are prepared in the background. You
                      will find this one on the Reports page when it is ready.
                    </p>
                  ) : null}

                  {/* actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className={PRIMARY_BTN}
                      disabled={Boolean(busy)}
                      onClick={() => run('download')}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      {busy === 'download'
                        ? 'Preparing…'
                        : canDownloadDirectly
                          ? 'Download PDF'
                          : 'Prepare report'}
                    </button>
                    <button
                      type="button"
                      className={SECONDARY_BTN}
                      disabled={Boolean(busy)}
                      onClick={() => run('email')}
                    >
                      <EnvelopeIcon className="w-4 h-4" />
                      {busy === 'email' ? 'Sending…' : 'Email it to me'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

ReportDownloadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(Object.values(REPORT_SCOPE)).isRequired,
  featureKey: PropTypes.string,
  featureLabel: PropTypes.string,
  plane: PropTypes.string,
  planeLabel: PropTypes.string,
  filters: PropTypes.object,
  onQueued: PropTypes.func,
  /**
   * Which data layer to talk to — api/companyReportApi.js by default,
   * api/adminReportApi.js for the super-admin panel. The two export the same
   * shape, so nothing else here changes.
   */
  api: PropTypes.object,
  /**
   * Who a super-admin report is about: `{ companyId }`, `{ userId }`, or
   * `{ accountType }` for the platform. Ignored by the company api, whose
   * subject is the company in the token.
   */
  subject: PropTypes.object
};

export default ReportDownloadDialog;
